import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Card, Row, Col, Container } from 'react-bootstrap';

interface AgentProfile {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    status: 'active' | 'inactive';
    joinDate: string;
    lastActive: string;
    totalTasks: number;
    completedTasks: number;
    avgCompletionTime: number;
    successRate: number;
    specializations: string[];
    recentActivity: Array<{
        id: string;
        action: string;
        timestamp: string;
        description: string;
    }>;
}

const AgentDetailsPage: React.FC = () => {
    const { id } = useParams<{ agentId: string }>();
    const [agent, setAgent] = useState<AgentProfile | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // TODO: Replace with actual API call
        const fetchAgentProfile = async () => {
            try {
                // Mock data - replace with actual API call
                const mockAgent: AgentProfile = {
                    id: id || '1',
                    name: 'Agent Smith',
                    email: 'agent.smith@company.com',
                    avatar: '/avatars/agent-smith.jpg',
                    status: 'active',
                    joinDate: '2024-01-15',
                    lastActive: '2024-03-15T10:30:00Z',
                    totalTasks: 156,
                    completedTasks: 142,
                    avgCompletionTime: 2.5,
                    successRate: 91.0,
                    specializations: ['Data Analysis', 'Report Generation', 'Customer Support'],
                    recentActivity: [
                        { id: '1', action: 'Task Completed', timestamp: '2024-03-15T09:30:00Z', description: 'Completed quarterly sales analysis' },
                        { id: '2', action: 'Task Started', timestamp: '2024-03-15T08:15:00Z', description: 'Started customer sentiment analysis' },
                        { id: '3', action: 'Report Generated', timestamp: '2024-03-14T16:45:00Z', description: 'Generated monthly performance report' }
                    ]
                };

                setAgent(mockAgent);
            } catch (error) {
                console.error('Error fetching agent profile:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAgentProfile();
    }, [id]);

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading...</div>;
    }

    if (!agent) {
        return <div className="text-center text-red-500">Agent not found</div>;
    }

    return (
        <Container>
            <div style={{ padding: '1.5rem' }}>
                {/* Header Section */}
                <Card className="mb-4">
                    <Card.Body className="p-4">
                        <Row className="align-items-center">
                            <Col xs={12} md={8} className="mb-3 mb-md-0">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                    <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                        {agent.avatar ? (
                                            <img src={agent.avatar} alt={agent.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <span style={{ fontSize: '1.125rem', fontWeight: '600', color: '#4b5563' }}>
                                                {agent.name.split(' ').map(n => n[0]).join('')}
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <h1 style={{ fontSize: '1.875rem', fontWeight: '700', marginBottom: '0.5rem' }}>{agent.name}</h1>
                                        <Badge bg={agent.status === 'active' ? 'success' : 'secondary'} style={{ marginBottom: '0.5rem' }}>
                                            {agent.status}
                                        </Badge>
                                        <p style={{ color: '#4b5563', margin: 0 }}>{agent.email}</p>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12} md={4}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="fa fa-calendar" style={{ color: '#3b82f6', minWidth: '1rem' }}></i>
                                        <span>Joined {new Date(agent.joinDate).toLocaleDateString()}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="fa fa-clock" style={{ color: '#3b82f6', minWidth: '1rem' }}></i>
                                        <span>Last active {new Date(agent.lastActive).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Performance Metrics */}
                <Row className="mb-4">
                    <Col xs={12} md={6} lg={3} className="mb-3">
                        <Card className="h-100">
                            <Card.Body>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#4b5563', marginBottom: '0.5rem' }}>Total Tasks</p>
                                        <p style={{ fontSize: '1.875rem', fontWeight: '700' }}>{agent.totalTasks}</p>
                                    </div>
                                    <i className="fa fa-bar-chart" style={{ fontSize: '1.75rem', color: '#3b82f6' }}></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={6} lg={3} className="mb-3">
                        <Card className="h-100">
                            <Card.Body>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#4b5563', marginBottom: '0.5rem' }}>Completed</p>
                                        <p style={{ fontSize: '1.875rem', fontWeight: '700' }}>{agent.completedTasks}</p>
                                    </div>
                                    <i className="fa fa-check-circle" style={{ fontSize: '1.75rem', color: '#10b981' }}></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={6} lg={3} className="mb-3">
                        <Card className="h-100">
                            <Card.Body>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#4b5563', marginBottom: '0.5rem' }}>Success Rate</p>
                                        <p style={{ fontSize: '1.875rem', fontWeight: '700' }}>{agent.successRate}%</p>
                                    </div>
                                    <i className="fa fa-chart-line" style={{ fontSize: '1.75rem', color: '#a855f7' }}></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={6} lg={3} className="mb-3">
                        <Card className="h-100">
                            <Card.Body>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#4b5563', marginBottom: '0.5rem' }}>Avg. Time (hrs)</p>
                                        <p style={{ fontSize: '1.875rem', fontWeight: '700' }}>{agent.avgCompletionTime}</p>
                                    </div>
                                    <i className="fa fa-hourglass-half" style={{ fontSize: '1.75rem', color: '#f97316' }}></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Specializations and Recent Activity */}
                <Row className="mb-4">
                    {/* Specializations */}
                    <Col xs={12} lg={6} className="mb-3">
                        <Card>
                            <Card.Header>
                                <Card.Title style={{ marginBottom: 0 }}>Specializations</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    {agent.specializations.map((spec, index) => (
                                        <Badge key={index} bg="info">{spec}</Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Recent Activity */}
                    <Col xs={12} lg={6} className="mb-3">
                        <Card>
                            <Card.Header>
                                <Card.Title style={{ marginBottom: 0 }}>Recent Activity</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {agent.recentActivity.map((activity) => (
                                        <div key={activity.id} style={{ display: 'flex', gap: '1rem' }}>
                                            <div style={{ width: '0.5rem', height: '0.5rem', backgroundColor: '#3b82f6', borderRadius: '50%', marginTop: '0.25rem', flexShrink: 0 }}></div>
                                            <div style={{ flex: 1 }}>
                                                <p style={{ fontSize: '0.875rem', fontWeight: '500', margin: 0, marginBottom: '0.25rem' }}>{activity.action}</p>
                                                <p style={{ fontSize: '0.75rem', color: '#4b5563', margin: 0, marginBottom: '0.25rem' }}>{activity.description}</p>
                                                <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>
                                                    {new Date(activity.timestamp).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Container>
    );
};

export default AgentDetailsPage;